import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, RefreshCw, Trash2, Zap } from 'lucide-react';
import { fetchAdminDlqUploads, retryAdminUpload, deleteAdminUpload } from '../../api/adminUploads';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

export function AdminUploadsDlq() {
  const queryClient = useQueryClient();
  const dlqQuery = useQuery({
    queryKey: ['admin-dlq-uploads'],
    queryFn: fetchAdminDlqUploads,
    refetchInterval: 30_000,
    retry: false,
  });

  const retryMutation = useMutation({
    mutationFn: ({ uploadId, force }: { uploadId: string; force?: boolean }) => retryAdminUpload(uploadId, force),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-dlq-uploads'] }),
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Retry failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (uploadId: string) => deleteAdminUpload(uploadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-dlq-uploads'] });
      toast.success('Deleted upload and metadata');
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Delete failed'),
  });

  const items = dlqQuery.data ?? [];

  return (
    <div className="min-h-screen pb-24 text-white"
         style={{
           background:
             'radial-gradient(circle at 6% 10%, rgba(99,102,241,0.18), transparent 52%), linear-gradient(180deg,#030712 0%,#020816 70%,#01020a 100%)',
         }}>
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 space-y-10">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Admin · Uploads</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">AV DLQ</h1>
            <p className="text-sm text-gray-400">
              Review failed antivirus scans. Retry, force a retry, or delete files and quarantine copies.
            </p>
          </div>
          <Button
            variant="secondary"
            className="bg-white/10 text-white hover:bg-white/20"
            onClick={() => dlqQuery.refetch()}
            disabled={dlqQuery.isFetching}
          >
            {dlqQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </header>

        {dlqQuery.isLoading && (
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading failed uploads…</span>
          </div>
        )}

        {dlqQuery.isError && (
          <div className="flex items-center gap-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-rose-200">
            <AlertTriangle className="h-4 w-4" />
            <div className="flex flex-col">
              <span className="font-medium">Failed to load DLQ. Please retry.</span>
              <span className="text-xs text-rose-200/80">Ensure API base URL is configured and you have admin access.</span>
            </div>
          </div>
        )}

        {!dlqQuery.isLoading && !dlqQuery.isError && items.length === 0 && (
          <Card className="border-white/10 bg-white/5 text-white shadow-lg shadow-amber-500/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckBadge />
                No failed scans
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-400">All uploads are healthy. Nothing to action.</CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {items.map((item) => (
            <Card key={item.id} className="border-white/10 bg-white/5 text-white">
              <CardHeader className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="text-lg font-semibold text-white">Upload {item.id}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
                    <Badge variant="secondary" className="bg-amber-500/20 text-amber-100">
                      Attempts {item.scanAttempts ?? 0}
                    </Badge>
                    {item.retryInSeconds !== undefined && (
                      <Badge variant="outline" className="border-white/20 text-white">
                        Retry in {item.retryInSeconds}s
                      </Badge>
                    )}
                    {item.nextRetryAt && <span className="text-xs text-gray-400">Next retry: {item.nextRetryAt}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300/40 bg-amber-200/10 text-amber-100"
                    onClick={() => retryMutation.mutate({ uploadId: item.id })}
                    disabled={retryMutation.isPending}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-amber-400 text-black hover:bg-amber-300"
                    onClick={() => retryMutation.mutate({ uploadId: item.id, force: true })}
                    disabled={retryMutation.isPending}
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    Force retry
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-200">
                <div className="space-y-1">
                  <div className="font-mono text-xs text-gray-400">key: {item.key}</div>
                  {item.quarantineKey && <div className="font-mono text-xs text-gray-400">quarantine: {item.quarantineKey}</div>}
                  {item.lastError && <div className="text-rose-200">Last error: {item.lastError}</div>}
                  {item.scanCompletedAt && <div className="text-gray-400">Last checked: {item.scanCompletedAt}</div>}
                </div>
                <Separator className="border-white/10" />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-400/50 text-red-200 hover:bg-red-500/10"
                    onClick={() => deleteMutation.mutate(item.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete file + metadata
                  </Button>
                  {item.retryUrl && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-300 underline decoration-dotted underline-offset-4"
                      onClick={() => navigator.clipboard.writeText(item.retryUrl ?? '').then(() => toast.success('Retry URL copied'))}
                    >
                      Copy retry URL
                    </Button>
                  )}
                  {item.forceRetryUrl && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-300 underline decoration-dotted underline-offset-4"
                      onClick={() =>
                        navigator.clipboard.writeText(item.forceRetryUrl ?? '').then(() => toast.success('Force retry URL copied'))
                      }
                    >
                      Copy force URL
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function CheckBadge() {
  return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/30 text-emerald-100">✓</span>;
}
